/**
 * client/src/pages/Shelf.tsx — the home screen (§6.5).
 *
 * Karti asked for a shelf and no subsystem design produced one. It carries: the cover, the title and
 * author, where you are in the book, narration coverage, the visibility chip, the ingest state, and
 * the "N books need confirming" row — because a batch upload that quietly produces mute books is the
 * failure §3.4 is organised against.
 *
 * ⚠️ **The rows come from `canList()` in `shared/access.ts` (LAW 13), not from a flag.** The server
 * already filters; filtering again here is not belt-and-braces, it is the *definition* being applied
 * in the place that renders it. `canList` vs `canSee` **is** what "unlisted" means: an unlisted book
 * you own appears on your shelf, and an unlisted book you merely hold the link to does not appear on
 * anyone's. Two implementations of that rule diverge on the case nobody screenshotted.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { canEdit, canList } from "../../../shared/access.ts";
import { AppFrame, BookCover, IconAlert, IconPlus } from "../App.tsx";
import { abApi } from "../lib/api.ts";
import type { ShelfCard, ShelfScope, ShelfSort } from "../lib/api.ts";
import { useAuth } from "../lib/auth.tsx";
import { useCachedQuery } from "../lib/idb.ts";
import { OfflineNotice, useOfflineBooks } from "./Offline.tsx";
import { ReadingTogetherRow, useMyRooms } from "./Rooms.tsx";

const SCOPES: { id: ShelfScope; label: string; hint: string }[] = [
  { id: "mine", label: "Mine", hint: "Books you uploaded" },
  { id: "public", label: "Public", hint: "Books anyone can find" },
  { id: "all", label: "All", hint: "Every book on the box" },
];

export function Shelf() {
  const { actor } = useAuth();
  // A newly-created account owns no books yet. Opening on `mine` therefore made the product look
  // empty at exactly the moment somebody first arrived, even though the family library was ready
  // to read. `all` means public + mine for an ordinary account and the complete library for god;
  // `Mine` stays one tap away for uploads and private books.
  const [scope, setScope] = useState<ShelfScope>("all");
  const [sort, setSort] = useState<ShelfSort>("recent");
  const [q, setQ] = useState("");

  // The query string is the cache key, so switching scopes paints the last answer for *that* scope
  // rather than the last answer for any scope.
  const key = `shelf:${scope}:${sort}:${q.trim().toLowerCase()}`;
  const shelf = useCachedQuery(key, () => abApi.shelf(scope, sort, q.trim()));

  /**
   * §5.8's offline filter.
   *
   * It reads the download manifest in IndexedDB, not the server's answer: "is this book on this
   * device" is a fact about local storage that no shelf response knows, and the whole point of the
   * filter is that it is right with the radio off. `useCachedQuery` has already painted the last
   * shelf from IndexedDB by then, so the filter has rows to work on with no network at all.
   */
  const offline = useOfflineBooks();
  const [offlineOnly, setOfflineOnly] = useState(false);

  const books = useMemo(() => {
    // God canList() archived rows so an admin recovery surface can expose them, but the ordinary
    // Library is not that surface. Showing a soft-deleted upload here made an archived exact
    // duplicate look live and left its card leading to a 404. Keep recovery authority intact while
    // keeping the reading shelf about books that can actually be opened.
    const listed = (shelf.data?.books ?? []).filter((b) => canList(b, actor) && !b.deletedAt);
    return offlineOnly ? listed.filter((b) => offline.ids.has(b.id)) : listed;
  }, [shelf.data, actor, offlineOnly, offline.ids]);

  const needsConfirming = useMemo(
    () => books.filter((b) => b.needsConfirm && b.editionState === "ready" && canEdit(b, actor)),
    [books, actor],
  );

  const continueReading = useMemo(() => {
    const withPos = books.filter((b) => b.position && b.editionState === "ready");
    if (withPos.length === 0) return null;
    return withPos.reduce((a, b) => (b.position!.updatedAt > a.position!.updatedAt ? b : a));
  }, [books]);

  // `all` is useful to everybody: public + owned books for a reader, every book for god. The server
  // applies the access predicate, so showing the tab never widens what a person may discover.
  const scopes = SCOPES;

  /**
   * §7.6, *Finding the room*: **a "Reading together" row on the shelf.**
   *
   * `<ReadingTogetherRow>` renders nothing when the list is empty — including when `/rooms/mine` is
   * not built yet, which `useMyRooms` reports as `unbuilt` rather than as an error — so a shelf
   * belonging to somebody who has never opened a room is byte-for-byte what it was before P5. That is
   * the same discipline `NarrationChrome` follows in the reader: a phase that is not in use pays
   * nothing and shows nothing.
   */
  const rooms = useMyRooms();

  return (
    <AppFrame wide>
      <div className="ab-shelf-head">
        <h1 className="ab-h1">Library</h1>

        <div className="ab-shelf-controls">
          <div className="ab-segment" role="tablist" aria-label="Which books">
            {scopes.map((s) => (
              <button
                key={s.id}
                type="button"
                role="tab"
                aria-selected={scope === s.id}
                title={s.hint}
                className={scope === s.id ? "ab-segment-item is-on" : "ab-segment-item"}
                onClick={() => setScope(s.id)}
              >
                {s.label}
              </button>
            ))}
          </div>

          <label className="ab-search">
            <span className="ab-sr">Search your books</span>
            <input
              type="search"
              value={q}
              placeholder="Title or author"
              autoCapitalize="none"
              autoCorrect="off"
              onChange={(e) => setQ(e.target.value)}
            />
          </label>

          {/* Absent when nothing is downloaded, rather than present and always empty — the same
              argument the `all` scope tab makes two lines up. */}
          {offline.books.length > 0 && (
            <button
              type="button"
              aria-pressed={offlineOnly}
              title="Only books stored on this device"
              className={offlineOnly ? "ab-segment-item is-on" : "ab-segment-item"}
              onClick={() => setOfflineOnly((v) => !v)}
            >
              On this device ({offline.books.length})
            </button>
          )}

          <label className="ab-select">
            <span className="ab-sr">Sort</span>
            <select value={sort} onChange={(e) => setSort(e.target.value as ShelfSort)}>
              <option value="recent">Recent</option>
              <option value="title">Title</option>
            </select>
          </label>
        </div>
      </div>

      {/* Offline is not an error, and it is not the same sentence as "the refresh failed". This row
          says which one it is and points at the one screen that works with no network — without
          which a shelf painted from last night's cache is indistinguishable from a broken one. */}
      <OfflineNotice what="this is the shelf as it was" />

      {/* An error over data we already have is a note, not a takeover. Blanking a shelf somebody was
          reading because a refresh failed is how an app reads as broken on a train. */}
      {shelf.error && shelf.data && (
        <p className="ab-note ab-note-warn">
          <IconAlert />
          <span>
            Showing the shelf as it was — {shelf.error.message}{" "}
            <button type="button" className="ab-link" onClick={shelf.reload}>
              Try again
            </button>
          </span>
        </p>
      )}

      {/* Above the books and below the confirm row: a room is a thing you are *in the middle of*,
          and it is the reason somebody opened the app on a Tuesday. Under the grid it would be
          below the fold on a phone, which for the one row with a live obligation attached is the
          wrong side of the screen. */}
      <ReadingTogetherRow rooms={rooms.rooms} />

      {needsConfirming.length > 0 && (
        <div className="ab-confirm-row-group">
          <Link to={`/b/${needsConfirming[0]!.id}/structure`} className="ab-confirm-row">
            <span className="ab-confirm-count">{needsConfirming.length}</span>
            <span className="ab-confirm-text">
              <strong>
                {needsConfirming.length === 1 ? "One book needs confirming" : `${needsConfirming.length} books need confirming`}
              </strong>
              <span>
                Check where each one begins before it is narrated in bulk — an unconfirmed book is
                exactly how three hundred pages of copyright page get read aloud.
              </span>
            </span>
          </Link>
          <ConfirmConfident books={needsConfirming} onDone={shelf.reload} />
        </div>
      )}

      {shelf.loading && !shelf.data ? (
        <ShelfSkeleton />
      ) : shelf.error && !shelf.data ? (
        <div className="ab-empty">
          <h2>The shelf didn&rsquo;t load</h2>
          <p>{shelf.error.message}</p>
          <button type="button" className="ab-btn ab-btn-primary" onClick={shelf.reload}>
            Try again
          </button>
        </div>
      ) : books.length === 0 ? (
        <EmptyShelf scope={scope} q={q} />
      ) : (
        <>
          {continueReading && scope !== "public" && !q && <ContinueCard card={continueReading} />}
          <div className="ab-grid">
            {books.map((b) => (
              <ShelfCardView key={b.id} card={b} />
            ))}
          </div>
        </>
      )}
    </AppFrame>
  );
}

// ─── confirming the ones the importer is sure about ──────────────────────────

/**
 * §3.4's gate is right and doing it 23 times is why nobody did it once.
 *
 * ⚠️ Measured on production two days after launch: **0 of 25 editions confirmed.** So LAW 10's
 * warm-up had never fired on any book — every first press of play was a cold ~40-second stare — and
 * the shelf had been showing *"23 books need confirming"* the whole time, linking to
 * `/b/<the first one>/structure`. One book at a time, no batch path. Nobody disagreed with
 * confirming; confirming was 23 errands.
 *
 * Two rules make this a shortcut rather than a "confirm everything" button:
 *
 *  · **Only ≥75%.** Below that the importer is telling you it guessed, and those books stay in the
 *    row above where a human will open them. On the live shelf the four books whose `bodyStartSid`
 *    was actually wrong all sat at 0.60–0.75, which is the whole argument for the threshold.
 *  · **The GPU is priced before it is spent**, from the planner's own estimate — the same shape as
 *    `/admin`'s lexicon promotion, which says *"affects 4 books · 902 sentences"* and then delivers
 *    exactly that. LAW 10 warms three pages per book, so confirming fourteen books queues
 *    forty-two; that number is a thing to agree to, not to discover.
 */
function ConfirmConfident({ books, onDone }: { books: ShelfCard[]; onDone: () => void }) {
  const [phase, setPhase] = useState<
    { k: "idle" } | { k: "pricing" } | { k: "quoted"; books: number; pages: number; estGpuSec: number } | { k: "running" } | { k: "error"; message: string }
  >({ k: "idle" });

  // The client's own copy of the server's floor. It is duplicated on purpose and the server is the
  // one that decides: this only keeps the button from offering a batch the route would refuse.
  const CONFIDENT = 0.75;
  const ids = useMemo(
    () =>
      books
        .map((b) => b.edition)
        .filter((e): e is NonNullable<ShelfCard["edition"]> => !!e && (e.bodyStartConfidence ?? 0) >= CONFIDENT)
        .map((e) => e.id),
    [books],
  );

  if (ids.length === 0) return null;

  const price = async () => {
    setPhase({ k: "pricing" });
    try {
      const res = await abApi.confirmMany(ids, CONFIDENT, true);
      if (res.books.length === 0) {
        setPhase({ k: "error", message: "None of these are ready to confirm without a look." });
        return;
      }
      setPhase({ k: "quoted", books: res.books.length, pages: res.pages, estGpuSec: res.estGpuSec });
    } catch (err) {
      setPhase({ k: "error", message: err instanceof Error ? err.message : "That didn't work." });
    }
  };

  const commit = async () => {
    setPhase({ k: "running" });
    try {
      await abApi.confirmMany(ids, CONFIDENT, false);
      setPhase({ k: "idle" });
      onDone();
    } catch (err) {
      setPhase({ k: "error", message: err instanceof Error ? err.message : "That didn't work." });
    }
  };

  return (
    <div className="ab-confirm-batch">
      {phase.k === "quoted" ? (
        <>
          <span className="ab-confirm-quote">
            Confirms <strong>{phase.books === 1 ? "1 book" : `${phase.books} books`}</strong> the importer is at
            least 75% sure about, and warms <strong>{phase.pages}</strong> {phase.pages === 1 ? "page" : "pages"} —
            about {minutes(phase.estGpuSec)} of narration. The rest stay in the list above.
          </span>
          <button type="button" className="ab-btn ab-btn-primary ab-btn-sm" onClick={() => void commit()}>
            Confirm them
          </button>
          <button type="button" className="ab-link" onClick={() => setPhase({ k: "idle" })}>
            Not now
          </button>
        </>
      ) : (
        <button
          type="button"
          className="ab-btn ab-btn-ghost ab-btn-sm"
          onClick={() => void price()}
          disabled={phase.k === "pricing" || phase.k === "running"}
        >
          {phase.k === "pricing"
            ? "Working out what that costs…"
            : phase.k === "running"
              ? "Confirming…"
              : `Confirm the ${ids.length} the importer is sure about`}
        </button>
      )}
      {phase.k === "error" && <span className="ab-hint ab-hint-bad">{phase.message}</span>}
    </div>
  );
}

/** GPU seconds, in the unit a person waits in. */
function minutes(sec: number): string {
  if (sec < 90) return `${Math.max(1, Math.round(sec))} seconds`;
  return `${Math.round(sec / 60)} minutes`;
}

// ─── one card ────────────────────────────────────────────────────────────────

/**
 * How far in you are.
 *
 * `position.ord / sentenceCount` — the `ord` projection (§2.1), which is dense, 0-based and
 * invalidated by nothing, because editions are immutable. **Not** `ms / totalMs`: `ms` is advisory
 * (LAW 3) and moves every time a page is narrated, so a progress bar built on it would creep
 * backwards on a book somebody is generating.
 */
function progressOf(card: ShelfCard): number | null {
  // ⚠️ `Number.isFinite`, not `<= 1`. `undefined <= 1` is **false**, so when this card's
  // `sentenceCount` went missing on the wire the guard did not fire — it fell through into
  // `position.ord / (undefined - 1)` and every card with a saved position rendered "NaN% · Chapter 1"
  // and a `style={{ width: "NaN%" }}` bar. Nothing threw and nothing was logged. The field is sent
  // now (routes/shelf.ts), and this guard refuses to render rather than render a lie if it ever
  // stops being.
  if (!card.position || !Number.isFinite(card.sentenceCount) || card.sentenceCount <= 1) return null;
  if (!Number.isFinite(card.position.ord)) return null;
  return Math.min(1, Math.max(0, card.position.ord / (card.sentenceCount - 1)));
}

function ShelfCardView({ card }: { card: ShelfCard }) {
  const pct = progressOf(card);
  const ingesting = card.editionState === "ingesting" || card.editionState === null;
  const failed = card.editionState === "failed";
  const to = failed || ingesting ? `/b/${card.id}` : `/b/${card.id}/read`;

  return (
    <Link to={to} className="ab-card">
      <div className="ab-card-cover">
        <BookCover bookId={card.id} title={card.title} author={card.author} coverSha={card.coverSha} />
        {pct !== null && (
          <div className="ab-card-progress" aria-hidden>
            <span style={{ width: `${Math.round(pct * 100)}%` }} />
          </div>
        )}
      </div>

      <div className="ab-card-body">
        <div className="ab-card-title">{card.title}</div>
        {card.author && <div className="ab-card-author">{card.author}</div>}

        <div className="ab-card-meta">
          {ingesting && <span className="ab-chip ab-chip-work">Reading the file…</span>}
          {failed && <span className="ab-chip ab-chip-bad">Import failed</span>}
          {!ingesting && !failed && pct !== null && (
            <span className="ab-chip">
              {Math.round(pct * 100)}% ·{" "}
              {card.position?.chapterTitle ?? `Chapter ${(card.position?.chapterOrd ?? 0) + 1}`}
            </span>
          )}
          {!ingesting && !failed && pct === null && Number.isFinite(card.chapterCount) && card.chapterCount > 0 && (
            <span className="ab-chip">{card.chapterCount} chapters</span>
          )}
          {card.visibility !== "private" && <span className="ab-chip ab-chip-quiet">{card.visibility}</span>}
          {card.needsConfirm && !ingesting && !failed && (
            <span className="ab-chip ab-chip-warn">Needs confirming</span>
          )}
          <NarrationChip card={card} />
        </div>
      </div>
    </Link>
  );
}

/**
 * Narration coverage.
 *
 * ⚠️ It never says "cached", to anyone. §6.5 accepts a cross-book *existence oracle* as a residual
 * risk on the strict condition that the UI reports `ready`/`building` and never reveals that
 * somebody else's book already produced this audio. The word matters; the mitigation is the word.
 */
function NarrationChip({ card }: { card: ShelfCard }) {
  if (!card.narration) return null;
  const { pagesWithAudio, pagesAudible } = card.narration;
  // ⚠️ `Number.isFinite`, not `=== 0`. LAW 9 says a missing numeric field is a hard failure and never
  // coerced — but the failure it produces HERE is not an exception, it is the string "NaN% narrated"
  // rendered on every card, which is what shipped when this file and `routes/shelf.ts` disagreed
  // about the field names. `undefined === 0` is false, so a name mismatch walked straight past the
  // old guard and into the arithmetic. This one refuses to render at all rather than render a lie.
  if (!Number.isFinite(pagesWithAudio) || !Number.isFinite(pagesAudible) || pagesAudible <= 0) return null;
  if (pagesWithAudio <= 0) return null;
  if (pagesWithAudio >= pagesAudible) return <span className="ab-chip ab-chip-voice">Narrated</span>;
  /**
   * ⚠️ **A count, not a percentage, and the denominator is `pagesAudible`.**
   *
   * The old chip divided by `totalPages` — every page of the edition, title pages included — so on
   * the live shelf *The Iliad* topped out at 87 %, *Alice* at 86 %, and **"Narrated" was unreachable
   * on every correctly-structured book in the library**. Two changes, and the second is why this is a
   * count: even against the right denominator, "93 % narrated" invites the question *which 7 %*, and
   * "38 of 41 pages have a voice" answers it. `pagesTotal` deliberately does not appear — it is the
   * number that made this wrong, and it has no business in an arithmetic on a shelf card.
   */
  return (
    <span className="ab-chip ab-chip-voice">
      {pagesWithAudio} of {pagesAudible} pages have a voice
    </span>
  );
}

function ContinueCard({ card }: { card: ShelfCard }) {
  const pct = progressOf(card);
  return (
    <Link to={`/b/${card.id}/read`} className="ab-continue">
      <div className="ab-continue-cover">
        <BookCover bookId={card.id} title={card.title} author={card.author} coverSha={card.coverSha} eager />
      </div>
      <div className="ab-continue-body">
        <span className="ab-eyebrow">Continue reading</span>
        <h2 className="ab-continue-title">{card.title}</h2>
        {card.author && <p className="ab-continue-author">{card.author}</p>}
        <p className="ab-continue-where">
          {card.position?.chapterTitle ?? `Chapter ${(card.position?.chapterOrd ?? 0) + 1}`}
          {pct !== null && <span className="ab-dot-sep"> · </span>}
          {pct !== null && `${Math.round(pct * 100)}% in`}
        </p>
        <span className="ab-btn ab-btn-primary ab-btn-sm">Open</span>
      </div>
    </Link>
  );
}

// ─── the states with no books in them ────────────────────────────────────────

function EmptyShelf({ scope, q }: { scope: ShelfScope; q: string }) {
  if (q) {
    return (
      <div className="ab-empty">
        <h2>Nothing matches &ldquo;{q}&rdquo;</h2>
        <p>Search runs over titles and authors. In-book search over every sentence lands in P6.</p>
      </div>
    );
  }
  if (scope !== "mine") {
    return (
      <div className="ab-empty">
        <h2>Nothing here yet</h2>
        <p>
          {scope === "public"
            ? "No book has been made public. A book becomes public only when Karti approves it — that is the whole of the takedown surface, and it is one column."
            : "No books are available to you yet. Ask Karti for access, or upload your own from Mine."}
        </p>
      </div>
    );
  }
  return (
    <div className="ab-empty ab-empty-first">
      <h2>Your library is empty.</h2>
      <p>
        Upload an EPUB and it becomes a book you can read on this phone in bed: one column per
        screen, turned rather than scrolled, your place kept as a sentence rather than a page number
        so it survives every font size you try.
      </p>
      <Link to="/upload" className="ab-btn ab-btn-primary">
        <IconPlus />
        <span>Upload a book</span>
      </Link>
      <p className="ab-empty-fine">EPUB, PDF, plain text and HTML.</p>
    </div>
  );
}

function ShelfSkeleton() {
  return (
    <div className="ab-grid" aria-hidden>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="ab-card ab-card-skeleton">
          <div className="ab-card-cover">
            <div className="ab-cover ab-cover-skeleton" />
          </div>
          <div className="ab-card-body">
            <div className="ab-skel-line" />
            <div className="ab-skel-line ab-skel-short" />
          </div>
        </div>
      ))}
    </div>
  );
}
